import { useMediaQuery, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import BaseModal from 'components/common/modals/BaseModal'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import RequestTxTrackerRerunModal from './RequestTxTrackerRerunModal'
import useTxTrackerQuickNavigation from './useTxTrackerQuickNavigation'
import SingleSTLView from './SingleSTLView'
import CompareSTLsView from './CompareSTLsView/CompareSTLsView'
import useTreatmentTracker from './useTreatmentTracker'
import { parseTxTrackerData } from '../trackingStampUtils'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    padding: 16
  },
  dialogContent: {
    padding: '0 24px 24px 24px!important'
  },
  disclaimerContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'inherit'
  },
  disclaimer: {
    paddingTop: 5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    color: '#54575D',
    fontSize: 14
  }
}))

const TreatmentTrackerModal = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const theme = useTheme()
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down('md'))

  const { patient } = useSelector(state => state.patientsReducer)
  const { isOpen, grinScanId } = useSelector(state => state.treatmentReducer.treatmentTrackerModal)
  const {
    alignerNumber,
    archModels,
    calculatedTreatmentType,
    endAlignerNumber,
    initAlignerNumber,
    scanNumber,
    isRetentionResult,
    retentionReferenceId
  } = useTreatmentTracker({
    grinScanId
  })
  const patientGrinScans = useSelector(state => state.patientsReducer.patient?.grinScans?.items || [])

  const [isRequestRerunModalOpen, setIsRequestRerunModalOpen] = useState(false)
  const [isCompareStlsModeOn, setIsCompareStlsModeOn] = useState(false)

  const {
    navigateToNextScan,
    navigateToPreviousScan,
    hasNextScan,
    hasPreviousScan,
    allTxTrackerScans,
    navigateToScanByScanId
  } = useTxTrackerQuickNavigation({
    currentGrinScanId: grinScanId,
    grinScans: patientGrinScans
  })

  const grinScan = useMemo(() => patientGrinScans.find(scan => scan.id === grinScanId), [grinScanId, patientGrinScans])
  const parsedTxTrackerData = useMemo(
    () => parseTxTrackerData(grinScan?.treatmentTrackerData),
    [grinScan?.treatmentTrackerData]
  )

  // Compare is an active-plan view: it lays the plan's stages against each other. The button
  // that opens it is already hidden for a retention result, but quick navigation can land on
  // one while compare is open, and it would then present the active plan as this scan's.
  // The state reset below runs only after render, so the render itself must also refuse
  // compare for a retention result or one frame of CompareSTLsView would mount for it.
  const effectiveCompareMode = isCompareStlsModeOn && !isRetentionResult

  useEffect(() => {
    if (isRetentionResult) {
      setIsCompareStlsModeOn(false)
    }
  }, [isRetentionResult, grinScanId])

  const handleClose = useCallback(() => {
    trackEvent('Treatment tracker popup - closed', {
      trackingMode: parsedTxTrackerData?.trackingMode,
      referenceId: parsedTxTrackerData?.referenceId
    })
    setIsCompareStlsModeOn(false)
    dispatch(Actions.setTreatmentTrackerModalOpen({ isOpen: false }))
  }, [dispatch, parsedTxTrackerData])

  const handleOpenRequestRerunModal = useCallback(() => {
    trackEvent('Treatment tracker - request rerun modal opened', {
      grinScanId: grinScan?.id,
      scanNumber,
      trackingMode: parsedTxTrackerData?.trackingMode,
      referenceId: parsedTxTrackerData?.referenceId
    })
    setIsRequestRerunModalOpen(true)
  }, [grinScan, scanNumber, parsedTxTrackerData])

  useHotkeys('ArrowDown', navigateToNextScan, { enabled: isOpen })
  useHotkeys('ArrowUp', navigateToPreviousScan, { enabled: isOpen })

  return (
    isOpen && (
      <>
        <BaseModal
          open={isOpen}
          handleClose={handleClose}
          className={classes.root}
          contentClassName={classes.dialogContent}
          disableEnforceFocus
        >
          <div className={classes.disclaimerContainer}>
            {effectiveCompareMode ? (
              <CompareSTLsView
                hasNextScan={hasNextScan}
                navigateToNextScan={navigateToNextScan}
                hasPreviousScan={hasPreviousScan}
                navigateToPreviousScan={navigateToPreviousScan}
                scanNumber={scanNumber}
                alignerNumber={alignerNumber}
                grinScan={grinScan}
                patient={patient}
                navigateToScanByScanId={navigateToScanByScanId}
                isCompareStlsModeOn={effectiveCompareMode}
                setIsCompareStlsModeOn={setIsCompareStlsModeOn}
                parsedTxTrackerData={parsedTxTrackerData}
                onOpenRequestRerunModal={handleOpenRequestRerunModal}
                archModels={archModels}
                calculatedTreatmentType={calculatedTreatmentType}
                isSmallerScreen={isSmallerScreen}
                initAlignerNumber={initAlignerNumber}
                endAlignerNumber={endAlignerNumber}
              />
            ) : (
              <SingleSTLView
                hasNextScan={hasNextScan}
                navigateToNextScan={navigateToNextScan}
                hasPreviousScan={hasPreviousScan}
                navigateToPreviousScan={navigateToPreviousScan}
                scanNumber={scanNumber}
                alignerNumber={alignerNumber}
                grinScan={grinScan}
                patient={patient}
                allTxTrackerScans={allTxTrackerScans}
                navigateToScanByScanId={navigateToScanByScanId}
                isCompareStlsModeOn={effectiveCompareMode}
                setIsCompareStlsModeOn={setIsCompareStlsModeOn}
                parsedTxTrackerData={parsedTxTrackerData}
                handleOpenRequestRerunModal={handleOpenRequestRerunModal}
                archModels={archModels}
                calculatedTreatmentType={calculatedTreatmentType}
                isSmallerScreen={isSmallerScreen}
                initAlignerNumber={initAlignerNumber}
                endAlignerNumber={endAlignerNumber}
                onOpenRequestRerunModal={handleOpenRequestRerunModal}
                isRetentionResult={isRetentionResult}
                retentionReferenceId={retentionReferenceId}
              />
            )}
            {!effectiveCompareMode && (
              <div className={classes.disclaimer}>{t('dialogs.treatmentTracker.disclaimer')}</div>
            )}
          </div>
        </BaseModal>
        <RequestTxTrackerRerunModal
          isOpen={isRequestRerunModalOpen}
          onClose={() => setIsRequestRerunModalOpen(false)}
          scanId={grinScan?.id}
          scanNumber={scanNumber}
          analyticsPayload={{
            grinScanId: grinScan?.id,
            scanNumber
          }}
        />
      </>
    )
  )
}

export default TreatmentTrackerModal
